import type { CSSProperties, MouseEventHandler, ReactNode } from 'react';
import { Children } from 'react';

/**
 * Primitives UI fusion V2 — Caption / Card / Badge / Button / Wordmark.
 *
 * Portées depuis `docs/prototypes/ui-kit-v2/ui_kits/the_chess_coach/Components.jsx`
 * en TypeScript strict + Tailwind. Tokens couleur viennent de `tailwind.config.ts`.
 */

// ─── Wordmark ─────────────────────────────────────────────────────────────

export function Wordmark(): JSX.Element {
  return (
    <span
      className="font-display font-bold text-paper-50 tracking-tight"
      style={{ letterSpacing: '-0.01em' }}
    >
      Th3 Ch€$S C0@ch
    </span>
  );
}

// ─── Caption ──────────────────────────────────────────────────────────────

export function Caption({
  children,
  color = 'ink',
  className = '',
}: {
  children: ReactNode;
  color?: 'ink' | 'paper' | 'violet' | 'cyan' | 'amber' | 'coral' | 'volt' | 'acid' | 'orange';
  className?: string;
}): JSX.Element {
  const colorClass = {
    ink: 'text-ink-300',
    paper: 'text-paper-50',
    violet: 'text-violet-500',
    cyan: 'text-cyan-500',
    amber: 'text-amber-500',
    coral: 'text-coral-500',
    volt: 'text-volt-500',
    acid: 'text-acid-500',
    orange: 'text-orange-500',
  }[color];
  return (
    <span
      className={`text-caption uppercase ${colorClass} ${className}`}
      style={{ letterSpacing: '0.05em', fontWeight: 500 }}
    >
      {children}
    </span>
  );
}

// ─── Card ──────────────────────────────────────────────────────────────────

export function Card({
  children,
  accent,
  padding = 'default',
  className = '',
  onClick,
  hoverable = false,
  style,
  delay,
}: {
  children: ReactNode;
  accent?: 'violet' | 'cyan' | 'amber' | 'coral' | 'volt' | 'acid' | 'orange';
  padding?: 'default' | 'compact' | 'large' | 'none';
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  hoverable?: boolean;
  style?: CSSProperties;
  /**
   * Décale l'animation `card-enter` pour produire un effet "tableau qui s'allume"
   * (proto V2 utilise +70ms par carte). Sans valeur, toutes les cards entrent
   * en même temps. Compatible `prefers-reduced-motion` via la règle globale.
   */
  delay?: number;
}): JSX.Element {
  const padClass = {
    default: 'p-6',
    compact: 'p-4',
    large: 'p-8',
    none: 'p-0',
  }[padding];
  const hoverClass = hoverable
    ? `cursor-pointer transition-all duration-150 hover:shadow-card-hover ${
        accent ? `hover:border-${accent}-500/40 hover:shadow-card-${accent}` : 'hover:border-ink-600'
      }`
    : '';
  const mergedStyle: CSSProperties =
    delay !== undefined ? { ...style, animationDelay: `${delay}ms` } : style ?? {};
  return (
    <div
      onClick={onClick}
      style={mergedStyle}
      className={`bg-ink-800 border border-ink-700 rounded-xl ${padClass} ${hoverClass} animate-card-enter ${className}`}
    >
      {children}
    </div>
  );
}

// ─── Badge ─────────────────────────────────────────────────────────────────

export function Badge({
  children,
  tone = 'paper',
  dot = false,
  className = '',
}: {
  children: ReactNode;
  tone?: 'paper' | 'muted' | 'violet' | 'cyan' | 'amber' | 'coral' | 'volt' | 'acid';
  dot?: boolean;
  className?: string;
}): JSX.Element {
  const toneClass = {
    paper: 'border-paper-50/30 text-paper-50 bg-paper-50/5',
    muted: 'border-ink-700 text-ink-300 bg-transparent',
    violet: 'border-violet-500/40 text-violet-500 bg-violet-500/10',
    cyan: 'border-cyan-500/40 text-cyan-500 bg-cyan-500/10',
    amber: 'border-amber-500/40 text-amber-500 bg-amber-500/10',
    coral: 'border-coral-500/40 text-coral-500 bg-coral-500/10',
    volt: 'border-volt-500/40 text-volt-500 bg-volt-500/10',
    acid: 'border-acid-500/40 text-acid-500 bg-acid-500/10',
  }[tone];
  const dotColor = {
    paper: 'bg-paper-50',
    muted: 'bg-ink-300',
    violet: 'bg-violet-500',
    cyan: 'bg-cyan-500',
    amber: 'bg-amber-500',
    coral: 'bg-coral-500',
    volt: 'bg-volt-500',
    acid: 'bg-acid-500',
  }[tone];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-mono ${toneClass} ${className}`}
    >
      {dot && <span className={`size-1.5 rounded-full ${dotColor}`} aria-hidden="true" />}
      {children}
    </span>
  );
}

// ─── Button ────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'compact' | 'default' | 'hero';

export function Button({
  children,
  variant = 'primary',
  size = 'default',
  accent,
  disabled = false,
  onClick,
  type = 'button',
  className = '',
}: {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  accent?: 'violet' | 'cyan' | 'amber' | 'coral' | 'volt' | 'acid' | 'orange';
  disabled?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}): JSX.Element {
  const sizeClass = {
    compact: 'h-8 px-3 text-xs',
    default: 'h-10 px-4 text-sm',
    hero: 'h-12 px-6 text-base',
  }[size];

  // Couleurs primary varient selon module accent.
  const primaryAccentBg = accent
    ? {
        violet: 'bg-violet-500',
        cyan: 'bg-cyan-500',
        amber: 'bg-amber-500',
        coral: 'bg-coral-500',
        volt: 'bg-volt-500',
        acid: 'bg-acid-500',
        orange: 'bg-orange-500',
      }[accent]
    : 'bg-acid-500';

  const variantClass = {
    primary: `${primaryAccentBg} text-ink-900 hover:opacity-90 active:opacity-80`,
    secondary: 'bg-transparent border border-ink-700 text-paper-50 hover:border-acid-500',
    ghost: 'bg-transparent text-paper-50 hover:bg-ink-800',
    danger: 'bg-transparent border border-coral-500 text-coral-500 hover:bg-coral-500/10',
  }[variant];

  const disabledClass = disabled ? 'opacity-40 cursor-not-allowed pointer-events-none' : '';

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={`font-display font-semibold rounded-lg inline-flex items-center justify-center gap-2 transition-all duration-150 ${sizeClass} ${variantClass} ${disabledClass} ${className}`}
      style={{ letterSpacing: '-0.005em' }}
    >
      {children}
    </button>
  );
}

// ─── Divider ───────────────────────────────────────────────────────────────

export function Divider({ vertical = false, className = '' }: { vertical?: boolean; className?: string }): JSX.Element {
  return vertical ? (
    <span className={`w-px self-stretch bg-ink-700 ${className}`} aria-hidden="true" />
  ) : (
    <hr className={`border-t border-ink-700 m-0 ${className}`} />
  );
}

// ─── StatStrip ─────────────────────────────────────────────────────────────
//
// Bandeau de stats horizontales pour les pages de liste (Opening Lab, Tactics,
// Endgame Forge). Composition fournie via children — pas de prescription forte.

export function StatStrip({ children, className = '' }: { children: ReactNode; className?: string }): JSX.Element {
  const items = Children.toArray(children);
  return (
    <div className={`flex flex-wrap items-center gap-x-6 gap-y-3 px-5 py-3 bg-ink-800 border border-ink-700 rounded-xl ${className}`}>
      {items.map((child, i) => (
        <div key={i} className="flex items-center gap-2">
          {child}
        </div>
      ))}
    </div>
  );
}
